import { BLENDER_PLUGIN_HEALTH_URL } from './config'

const PROBE_TIMEOUT_MS = 1500

/**
 * Issues a short-lived HTTP GET against the Blender plugin's `/health`
 * endpoint to detect whether the plugin is currently listening, without
 * consuming the plugin's single WebSocket client slot (so a probe never
 * disturbs an in-flight send).
 *
 * Uses `mode: 'no-cors'` because the plugin's healthcheck does not advertise
 * CORS headers; we don't need to read the body, only know whether the request
 * reached a live server. A resolved fetch implies the plugin replied (any
 * status); a rejected fetch implies the connection was refused or aborted.
 *
 * Why native `fetch` and not the project's axios-based `useApiGet` helper:
 *   1. `useApiGet` calls `captureException` on every error. "Plugin not
 *      running" is the *expected* steady state when no DCC plugin is
 *      installed; routing it through Sentry would generate a flood of false
 *      positives at the popover's polling cadence.
 *   2. `useApiGet` attaches our CSRF token and `withCredentials: true` to
 *      every request. Both are meaningless against `localhost:49485` and
 *      represent a small unnecessary leak of session context to whatever
 *      process happens to be bound to that port.
 *   3. Axios runs on top of XHR, which has no equivalent of fetch's
 *      `mode: 'no-cors'`. Without that mode the browser would reject the
 *      response for missing CORS headers and the probe would always fail.
 *   4. `useApiGet` is a React hook and exposes loading state for UI; this
 *      probe is a fire-and-forget background check with no spinner.
 *
 * @param {(connected: boolean) => void} onResult
 * @returns {{ close: () => void }}
 */
export const probeBlender = (onResult) => {
  let settled = false
  let timeoutId
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null

  const settle = (connected) => {
    if (settled) return
    settled = true
    if (timeoutId) clearTimeout(timeoutId)
    onResult(connected)
  }

  timeoutId = setTimeout(() => {
    if (controller) controller.abort()
    settle(false)
  }, PROBE_TIMEOUT_MS)

  try {
    fetch(BLENDER_PLUGIN_HEALTH_URL, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller ? controller.signal : undefined,
    })
      .then(() => settle(true))
      .catch(() => settle(false))
  } catch {
    settle(false)
  }

  return {
    close: () => {
      if (controller) controller.abort()
      settle(false)
    },
  }
}
